// RefundPolicy.jsx

import React, { useState, useEffect } from "react";
import { Link as ScrollLink } from "react-scroll";
import Header from "../components/layout/Header";
import LegalFooter from "../components/common/LegalFooter";
import styles from "./RefundPolicy.module.scss";

const icons = {
  document: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#d08b1d"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
    </svg>
  ),

  shield: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#d08b1d"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
      <path d="M9 12l2 2 4-4"></path>
    </svg>
  ),

  calendar: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#d08b1d"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  ),

  clock: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#d08b1d"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  ),

  info: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#d08b1d"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="16" x2="12" y2="12"></line>
      <line x1="12" y1="8" x2="12.01" y2="8"></line>
    </svg>
  ),

  folder: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#d08b1d"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
    </svg>
  )
};

const clauses = [
  {
    id: "01",
    title: "Nature of Our Services",
    icon: icons.document,
    content:
      "We provide business reporting, analytics, dashboards, advisory support, and clarity-driven operational services."
  },

  {
    id: "02",
    title: "General Refund Principle",
    icon: icons.shield,
    content:
      "Refunds are generally not applicable once onboarding, setup, reporting, or business review work has started."
  },

  {
    id: "03",
    title: "What “Service Commencement” Means",
    icon: icons.calendar,
    content:
      "Service commencement means onboarding, setup, access provisioning, review activity, or reporting work has started."
  },

  {
    id: "04",
    title: "Cancellation Before Service Commencement",
    icon: icons.clock,
    content:
      "Refunds before commencement may be considered subject to deductions for payment gateway and admin charges."
  },

  {
    id: "05",
    title: "Cancellation After Service Commencement",
    icon: icons.info,
    content:
      "Once service effort has started, refund eligibility becomes highly restricted."
  },

  {
    id: "06",
    title: "Customised and Prepared Work Is Generally Non-Refundable",
    icon: icons.folder,
    content:
      "Custom reports, prepared dashboards, configured access, and structured business material are non-refundable."
  },

  {
    id: "07",
    title: "Recurring Plans, Weekly Plans, Monthly Plans, and Auto-Debit",
    icon: icons.calendar,
    content:
      "Cancellations apply to future cycles only; currently active cycles and automated renewals are non-refundable."
  },

  {
    id: "08",
    title: "Non-Usage Does Not Automatically Create Refund Eligibility",
    icon: icons.info,
    content:
      "Refunds are not granted solely based on non-usage, failure to log in, or delayed cooperation from your side."
  },

  {
    id: "09",
    title: "Delay or Non-Cooperation from Client Side",
    icon: icons.clock,
    content:
      "Delays or interruptions caused by missing data or non-responsiveness from your team do not create refund eligibility."
  },

  {
    id: "10",
    title: "No Refund Based on Business Results or Expectations",
    icon: icons.document,
    content:
      "Services are provided for business clarity and support, not for guaranteed financial outcomes or profit increases."
  },

  {
    id: "11",
    title: "Duplicate Payments and Excess Payments",
    icon: icons.folder,
    content:
      "Excess or duplicate payments will be refunded after verification, subject to applicable deductions."
  },

  {
    id: "12",
    title: "Failed Transactions and Payment Errors",
    icon: icons.shield,
    content:
      "Munafa Engine is not responsible for third-party delays in resolving failed transactions or payment errors."
  },

  {
    id: "13",
    title: "Refund Mode and Processing Time",
    icon: icons.clock,
    content:
      "Approved refunds are processed through the original payment mode, with timelines depending on banking cycles."
  },

  {
    id: "14",
    title: "Payment Gateway Charges, Taxes, and Non-Recoverable Deductions",
    icon: icons.document,
    content:
      "Approved refunds may be subject to deductions for non-recoverable charges, taxes, and incurred costs."
  },

  {
    id: "15",
    title: "Chargebacks and Payment Disputes",
    icon: icons.shield,
    content:
      "Initiating a chargeback while service has commenced does not entitle you to an automatic refund."
  },

  {
    id: "16",
    title: "Cancellation, Suspension, or Termination by Munafa Engine",
    icon: icons.info,
    content:
      "We may suspend or terminate service for misuse or compliance risks, with refunds determined by the completion stage."
  },

  {
    id: "17",
    title: "Service Access Suspension and Its Refund Effect",
    icon: icons.shield,
    content:
      "Suspensions due to terms violations or unauthorized access do not automatically make you eligible for a refund."
  },

  {
    id: "18",
    title: "Goodwill, Exception-Based, or Discretionary Refunds",
    icon: icons.folder,
    content:
      "Exception-based refunds may be offered entirely at our discretion and do not create a precedent."
  },

  {
    id: "19",
    title: "No Waiver by Delayed Action",
    icon: icons.clock,
    content:
      "Delayed enforcement of this policy does not constitute a waiver of our rights in future situations."
  },

  {
    id: "20",
    title: "Changes to This Policy",
    icon: icons.document,
    content:
      "This policy may be updated periodically to reflect changes in our services, compliance, or billing structures."
  },

  {
    id: "21",
    title: "Contact for Cancellation or Refund Requests",
    icon: icons.info,
    content:
      "Please contact us at connect@munafaengine.com with relevant details for any cancellation or refund requests."
  },

  {
    id: "22",
    title: "Final Note",
    icon: icons.document,
    content:
      "By making a payment or starting an engagement, you fully acknowledge and agree to this policy."
  }
];

export default function RefundPolicy() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);

  const [expandedClause, setExpandedClause] = useState("01");

  useEffect(() => {
    const handleResize = () =>
      setIsMobile(window.innerWidth <= 992);

    window.addEventListener("resize", handleResize);

    return () =>
      window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={styles.pageWrapper}>
      <Header />

      <main className={styles.mainContent}>
        <div className={styles.container}>
          {/* TOP SECTION */}

          <div className={styles.topSection}>
            <div className={styles.leftIntro}>
              <span className={styles.badge}>
                LEGAL PAGE
              </span>

              <h1 className={styles.title}>
                Refund & Cancellation Policy
              </h1>

              <p className={styles.intro}>
                This Refund & Cancellation Policy explains
                how cancellations, rescheduling,
                recurring billing, auto-debit,
                and refund requests are handled
                for services offered under Munafa Engine.
              </p>

              <p className={styles.intro}>
                By making a payment, onboarding,
                or continuing with a paid engagement,
                you agree to this Refund & Cancellation Policy
                along with our Terms & Conditions
                and Privacy Policy.
              </p>
            </div>

            <div className={styles.rightHero}>
              <img
                src={require("../images/Refund & Cancellation.webp")}
                alt="Refund Policy"
                className={styles.heroImage}
               loading="lazy" decoding="async"/>
            </div>
          </div>

          {/* BOTTOM SECTION */}

          <div className={styles.bottomSection}>
            {!isMobile && (
              <div className={styles.sidebar}>
                <div className={styles.sidebarSticky}>
                  <div className={styles.sidebarHeader}>
                    <h2>Quick Navigation</h2>

                    <span>22 Clauses</span>
                  </div>

                  <nav className={styles.navLinks}>
                    {clauses.map((clause) => (
                      <ScrollLink
                        key={clause.id}
                        to={`clause-${clause.id}`}
                        smooth={true}
                        duration={500}
                        offset={-100}
                        className={styles.navLink}
                      >
                        <span className={styles.navId}>
                          {clause.id}
                        </span>

                        <span className={styles.navTitle}>
                          {clause.title}
                        </span>
                      </ScrollLink>
                    ))}
                  </nav>
                </div>
              </div>
            )}

            <div className={styles.clausesContent}>
              {isMobile ? (
                <div className={styles.mobileAccordion}>
                  <div className={styles.accordionHeader}>
                    <h2>Contents</h2>

                    <span>22 Clauses</span>
                  </div>

                  <div className={styles.accordionList}>
                    {clauses.map((clause) => (
                      <div
                        key={clause.id}
                        className={styles.accordionItem}
                      >
                        <button
                          className={styles.accordionToggle}
                          onClick={() =>
                            setExpandedClause(
                              expandedClause === clause.id
                                ? null
                                : clause.id
                            )
                          }
                        >
                          <div
                            className={
                              styles.accordionToggleLeft
                            }
                          >
                            <span
                              className={styles.accordionId}
                            >
                              {clause.id}
                            </span>

                            <span
                              className={
                                styles.accordionTitle
                              }
                            >
                              {clause.title}
                            </span>
                          </div>

                          <span
                            className={
                              styles.accordionIcon
                            }
                          >
                            {expandedClause === clause.id
                              ? "−"
                              : "+"}
                          </span>
                        </button>

                        {expandedClause === clause.id && (
                          <div
                            className={
                              styles.accordionContent
                            }
                          >
                            {clause.content}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={styles.clausesList}>
                  {clauses.map((clause) => (
                    <div
                      id={`clause-${clause.id}`}
                      key={clause.id}
                      className={styles.clauseCard}
                    >
                      <div
                        className={styles.clauseIconWrapper}
                      >
                        {clause.icon}
                      </div>

                      <div className={styles.clauseBody}>
                        <div
                          className={
                            styles.clauseTitleRow
                          }
                        >
                          <span
                            className={styles.clauseId}
                          >
                            {clause.id}
                          </span>

                          <h3
                            className={styles.clauseTitle}
                          >
                            {clause.title}
                          </h3>
                        </div>

                        <div
                          className={styles.clauseText}
                        >
                          {clause.content}
                        </div>
                      </div>
                    </div>
                  ))}


                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <div className={styles.securityBar}>
        <div className={styles.securityContainer}>
          <div className={styles.securityIconWrapper}>
            {icons.shield}
          </div>

          <span className={styles.securityText}>
            Your data is private, secure, and never shared.
          </span>
        </div>
      </div>

      <LegalFooter />
    </div>
  );
}